import { useMemo } from "react";

import { normalizeJsonFormSchema } from "containers/admin/clients/client/components/form-builder/form-schema";
import { isDynamicFormSchema } from "containers/admin/clients/client/programs-page/program-strategy-form.utils";
import {
  buildCreativeRows,
  buildSectionSummaryFields,
  CAMPAIGN_DETAILS_FIELD_NAMES,
  CAMPAIGN_LOGIC_FIELD_NAMES,
  type CreativeRow,
  type SummaryField,
} from "containers/admin/clients/client/campaign-templates-page/campaign-template-summary.utils";

import { useCampaignTemplate, useCampaignTemplateSchema } from "hooks/api/use-campaign-templates";

const CAMPAIGN_DETAILS_PATH = ["campaign-details"];

const CAMPAIGN_LOGIC_PATH = ["campaign-details", "campaignLogic"];

export type UseCampaignTemplateSummaryResult = {
  title: string;
  isLoading: boolean;
  generalFields: SummaryField[];
  logicFields: SummaryField[];
  creativeRows: CreativeRow[];
};

/**
 * A summary can be built either from a Campaign Template (fetched by id) or directly from a
 * campaign's own strategy — same schema-driven shape, just already in hand as `schemaVersion` +
 * `data` (e.g. `campaign.schemaVersion` / `campaign.strategyData`), with no template id available.
 */
export type CampaignSummarySource =
  | { type: "template"; templateId: string }
  | { type: "data"; name: string; schemaVersion?: number; data?: unknown };

export const useCampaignTemplateSummary = (
  source: CampaignSummarySource,
): UseCampaignTemplateSummaryResult => {
  const isTemplate = source.type === "template";

  const { data: template, isLoading: isTemplateLoading } = useCampaignTemplate(
    isTemplate ? source.templateId : undefined,
  );

  const title = isTemplate ? (template?.name ?? "") : source.name;
  const schemaVersion = isTemplate ? template?.schemaVersion : source.schemaVersion;
  const data = isTemplate ? template?.data : source.data;

  const { data: schemaResponse, isLoading: isSchemaLoading } = useCampaignTemplateSchema(
    { version: schemaVersion },
    { enabled: Boolean(schemaVersion) },
  );

  const schema = useMemo(
    () =>
      isDynamicFormSchema(schemaResponse) ? normalizeJsonFormSchema(schemaResponse) : undefined,
    [schemaResponse],
  );

  const generalFields = useMemo(
    () =>
      schema
        ? buildSectionSummaryFields(schema, data, CAMPAIGN_DETAILS_PATH, CAMPAIGN_DETAILS_FIELD_NAMES)
        : [],
    [schema, data],
  );

  const logicFields = useMemo(
    () =>
      schema
        ? buildSectionSummaryFields(schema, data, CAMPAIGN_LOGIC_PATH, CAMPAIGN_LOGIC_FIELD_NAMES)
        : [],
    [schema, data],
  );

  const creativeRows = useMemo(() => (schema ? buildCreativeRows(schema, data) : []), [schema, data]);

  return {
    title,
    isLoading: (isTemplate && isTemplateLoading) || (Boolean(schemaVersion) && isSchemaLoading),
    generalFields,
    logicFields,
    creativeRows,
  };
};
